import React, { useMemo, useContext } from "react";
import { FaGlobe } from "react-icons/fa";
import { StoreContext } from "../../context/StoreContext";
import { SUPPORTED_PLATFORM_LANGUAGES, getLanguageByCode } from "../../utils/languageConfig";

type SupportedLanguage = string;

interface LanguageSwitcherProps {
 currentLanguage: SupportedLanguage;
 onLanguageChange: (language: SupportedLanguage) => void;
 showLabel?: boolean;
 className?: string;
}

export const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({
 currentLanguage,
 onLanguageChange,
 showLabel = true,
 className = "",
}) => {
 // Safely access store context - returns undefined if provider is not available (platform pages)
 const storeContext = useContext(StoreContext);
 const store = storeContext?.store;

 // Get supported languages from store, default to supported platform languages if store context is not available
 const supportedLanguages = useMemo(() => {
  // If store context is not available (platform pages), show all supported platform languages
  if (!storeContext || !store) {
   return SUPPORTED_PLATFORM_LANGUAGES.map((lang) => lang.code);
  }
  const storeLanguages = store.supportedLanguages || ["en"];
  // Only return languages that are both in store's supported languages AND supported by platform
  return storeLanguages.filter((code) => 
    SUPPORTED_PLATFORM_LANGUAGES.some(lang => lang.code === code)
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [storeContext, store?.supportedLanguages, store]);

 // Don't render if only one language is supported and we have a store context
 if (supportedLanguages.length <= 1 && storeContext && store) {
  return null;
 }

 return (
  <div className={`flex items-center gap-2 ${className}`}>
   {showLabel && (
    <div className="flex items-center gap-2 text-sm text-gray-600">
     <FaGlobe className="text-base" />
     <span className="font-medium">Language:</span>
    </div>
   )}
   <div className="flex gap-1 bg-gray-100 p-1">
    {supportedLanguages.map((lang) => (
     <button
      key={lang}
      onClick={(e) => {
       e.preventDefault();
       e.stopPropagation();
       onLanguageChange(lang);
      }}
      className={`px-3 py-1.5 text-sm font-medium transition-colors ${
       currentLanguage === lang
        ? "bg-blue-600 text-white "
        : "text-gray-600 hover:bg-gray-200"
      }`}
      title={getLanguageByCode(lang)?.label || lang.toUpperCase()}
      type="button"
     >
      {getLanguageByCode(lang)?.flag || ""} {lang.toUpperCase()}
     </button>
    ))}
   </div>
  </div>
 );
};

